import { useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const blank = { name: "", mobile: "", gstin: "", remarks: "", status: "open" };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== "ALL") params.status_filter = statusFilter;
      const { data } = await api.get("/customers", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(fetchItems, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, statusFilter]);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, mobile: c.mobile, gstin: c.gstin || "", remarks: c.remarks || "", status: c.status });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Name and mobile required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/customers/${editing.id}`, form);
        setItems((prev) => prev.map((it) => (it.id === editing.id ? data : it)));
        toast.success("Customer updated");
      } else {
        const { data } = await api.post("/customers", form);
        setItems((prev) => [data, ...prev]);
        toast.success("Customer added — we'll remind you to follow up every 5 days");
      }
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    const id = deleteId;
    const prev = items;
    setItems((p) => p.filter((x) => x.id !== id));
    setDeleteId(null);
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer removed");
    } catch (e) {
      setItems(prev);
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">CRM · Leads</div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">Customers & Follow-ups</h1>
          <p className="text-sm text-[#475569] mt-1">Capture prospects, track follow-ups, and convert to sales without missing a beat.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="add-customer-btn">
          <Plus className="w-4 h-4" /> New customer
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input placeholder="Search by name, mobile, remarks…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="customers-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-10 sm:w-44" data-testid="customers-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="open">Open (active)</SelectItem>
            <SelectItem value="closed">Closed (sold)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-[#F8F9FA]">
            <TableRow>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Name</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Mobile</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Remarks</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Added</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-[#64748B]">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-[#64748B]" data-testid="customers-empty">
                No customers yet. Click "New customer" to start tracking leads.
              </TableCell></TableRow>
            ) : items.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                <TableCell className="text-sm font-medium">{c.name}</TableCell>
                <TableCell className="font-mono text-xs">{c.mobile}</TableCell>
                <TableCell className="text-xs text-[#475569] max-w-[300px] truncate" title={c.remarks}>{c.remarks || "—"}</TableCell>
                <TableCell><StatusBadge status={c.status === "open" ? "Open" : "Completed"} /></TableCell>
                <TableCell className="font-mono text-xs text-[#64748B]">{(c.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(c.id)} data-testid={`delete-customer-${c.id}`}><Trash2 className="w-4 h-4 text-[#EF4444]" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="customer-sheet">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit customer" : "New customer"}</DialogTitle>
            <DialogDescription>{editing ? "Update lead details and status." : "Capture name, mobile and notes. We'll remind every 5 days while status is Open."}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Mobile *</Label>
                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="font-mono" data-testid="customer-mobile" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">GSTIN</Label>
                <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} className="font-mono" placeholder="22AAAAA0000A1Z5" maxLength={15} data-testid="customer-gstin" />
                <p className="text-[11px] text-[#64748B]">Auto-fills into quotations for this customer.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="customer-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open (follow up)</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-[#475569]">Remarks</Label>
              <Textarea rows={3} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Looking for ThinkPad T-series under 30k, willing to wait 1 week…" data-testid="customer-remarks" />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="customer-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040C1] text-white" data-testid="customer-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Save changes" : "Add customer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the customer record.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-customer-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
